import type {
  InvoiceFormData,
  InvoiceLineItem,
  PartyEntry,
  DateEntry,
  ReferenceEntry,
  AllowanceCharge,
} from '../types/invoice';
import type { TemplateConfig } from '../types/tradingPartner';

/**
 * Build a fresh invoice line item, honoring the (overlaid) template config:
 *   - line UOM from defaults.lineItems.unitOfMeasure (Natalie: "PIECES") — was
 *     previously hardcoded to 'EA', so the invoice UOM default never took effect.
 *   - part rows from prePopulateRequirements.lineItems.addPartInfoByDefault,
 *     with optional pre-filled `id` (e.g. { qualifier: 'CH', id: 'US' }).
 * Pass the overlaid config so customer_defaults (WDEV-142) win.
 */
export function createInvoiceLineItem(lineNumber: number, config?: TemplateConfig): InvoiceLineItem {
  let defaultParts = [{ qualifier: 'UP', id: '' }];
  const addPartInfo = config?.prePopulateRequirements?.lineItems?.addPartInfoByDefault;
  if (addPartInfo) {
    if (Array.isArray(addPartInfo)) {
      defaultParts = addPartInfo.map((part) => ({ qualifier: part.qualifier, id: part.id ?? '' }));
    } else if (config?.templates?.partInfo?.commodityCodeQualifier) {
      defaultParts = [{ qualifier: config.templates.partInfo.commodityCodeQualifier, id: '' }];
    }
  }
  return {
    lineNumber,
    quantity: '',
    uom: config?.defaults?.lineItems?.unitOfMeasure ?? 'EA',
    unitPrice: '',
    extendedAmount: '0.00',
    parts: defaultParts,
    description: '',
    references: [],
    pricing: [],
    taxes: [],
    allowancesCharges: [],
  };
}

/**
 * WDEV-51: invoice-side counterpart to `templateApplicator.applyTemplateToFormData`.
 *
 * Seeds a new-doc `InvoiceFormData` from a TemplateConfig:
 *   - defaults.header              → header fields (currencyCode, paymentTerms, etc.)
 *   - prePopulateRequirements:
 *       parties.addByDefault       → formData.names (PartyEntry[])
 *       dates.addByDefault         → formData.dates
 *       references.addByDefault    → formData.references
 *       headerAllowancesCharges    → formData.headerAllowancesCharges
 *
 * All seeded rows are plain editable defaults — the user can change any
 * value or remove any row before saving. Nothing is locked.
 */
export function applyInvoiceTemplate(
  baseFormData: InvoiceFormData,
  templateConfig: TemplateConfig,
): InvoiceFormData {
  const { defaults, prePopulateRequirements, templates } = templateConfig;

  // ── Header defaults ─────────────────────────────────────────────────────
  // Merge any keys the template declares in defaults.header. Undefined
  // values are skipped so an empty string in the template doesn't nuke a
  // real value already on the form.
  const updated: InvoiceFormData = { ...baseFormData };
  if (defaults?.header) {
    for (const [key, value] of Object.entries(defaults.header)) {
      if (value !== undefined) {
        (updated as unknown as Record<string, unknown>)[key] = value;
      }
    }
  }

  if (!prePopulateRequirements) return updated;

  // ── Parties (names) ─────────────────────────────────────────────────────
  if (prePopulateRequirements.parties?.addByDefault) {
    const parties: PartyEntry[] = prePopulateRequirements.parties.addByDefault.map((req) => {
      const entry: PartyEntry = {
        type: req.type,
        name: req.name ?? '',
        qualifier: req.qualifier ?? templates.name.identificationCodeQualifier ?? '',
        id: req.id ?? '',
      };
      if (req.address1 !== undefined)  entry.address1  = req.address1;
      if (req.address2 !== undefined)  entry.address2  = req.address2;
      if (req.city !== undefined)      entry.city      = req.city;
      if (req.state !== undefined)     entry.state     = req.state;
      if (req.postalCode !== undefined) entry.postalCode = req.postalCode;
      if (req.country !== undefined)   entry.country   = req.country;
      return entry;
    });
    updated.names = parties;
  }

  // ── Dates ───────────────────────────────────────────────────────────────
  if (prePopulateRequirements.dates?.addByDefault) {
    const dates: DateEntry[] = prePopulateRequirements.dates.addByDefault.map((d) => ({
      qualifier: d.qualifier,
      date: '',
    }));
    updated.dates = dates;
  }

  // ── References ──────────────────────────────────────────────────────────
  if (prePopulateRequirements.references?.addByDefault) {
    const references: ReferenceEntry[] = prePopulateRequirements.references.addByDefault.map((r) => ({
      qualifier: r.qualifier,
      id: '',
    }));
    updated.references = references;
  }

  // ── Header allowances / charges ─────────────────────────────────────────
  if (prePopulateRequirements.headerAllowancesCharges?.length) {
    const headerAC: AllowanceCharge[] = prePopulateRequirements.headerAllowancesCharges.map((ac) => {
      const row: AllowanceCharge = {
        indicator: ac.indicator,
        code: ac.code,
        amount: ac.amount,
      };
      if (ac.description !== undefined) row.description = ac.description;
      if (ac.percent !== undefined) row.percent = ac.percent;
      return row;
    });
    updated.headerAllowancesCharges = headerAC;
  }

  return updated;
}
